import { Action, createReducer, on } from '@ngrx/store';
import { MasterData } from 'src/app/models/masterdata';
import {
    deleteOutdatedHorizon,
    errorDeleteOutdatedHorizon,
    errorFetchAllMasterData,
    errorUpdateOutdatedHorizon,
    fetchAllMasterData,
    successDeleteOutdatedHorizon,
    successFetchAllMasterData,
    successUpdateOutdatedHorizon,
    updateOutdatedHorizon,
} from '../actions/masterdata.action';
import { Scope } from 'src/app/models/profile';

export interface State {
    data: MasterData;
    loading: boolean;
    loaded: boolean;
    horizonUpdating: boolean;
    horizonDeleting: boolean;
    horizonError: unknown;
}

// default state
const initialState: State = {
    data: {
        status: [],
        category: {
            [Scope.I3M]: { family: [], parentFamily: [] },
            [Scope.LOG]: { family: [], parentFamily: [] },
            [Scope.OVERSIZE]: { family: [], parentFamily: [] },
            [Scope.ACT]: { family: [], parentFamily: [] },
            [Scope.CATracking]: { family: [], parentFamily: [] }
        },
        program: [],
        zoneType: {
            [Scope.I3M]: [],
            [Scope.LOG]: [],
            [Scope.OVERSIZE]: [],
            [Scope.ACT]: [],
        },
        adminRoles: [],
        categorySignalSource: {
            [Scope.I3M]: [],
            [Scope.LOG]: [],
            [Scope.OVERSIZE]: [],
            [Scope.ACT]: [],
        },
        outdatedHorizons: [],
    },
    loading: false,
    loaded: false,
    horizonUpdating: false,
    horizonDeleting: false,
    horizonError: null,
};

const masterDataReducer = createReducer(
    initialState,
    on(fetchAllMasterData, (state): State => {
        return {
            ...state,
            loading: true,
            loaded: false
        };
    }),
    on(successFetchAllMasterData, (state, action) => {
        return {
            ...state,
            loading: false,
            loaded: true,
            data: (() => {
                state.data = action.masterData;
                return state.data;
            })()
        };
    }),
    on(errorFetchAllMasterData, (state): State => {
        return {
            ...state,
            loading: false,
            loaded: true
        };
    }),
    on(updateOutdatedHorizon, (state): State => ({
        ...state,
        horizonUpdating: true,
        horizonDeleting: false,
        horizonError: null,
    })),
    on(successUpdateOutdatedHorizon, (state, action): State => {
        const existingHorizons = state.data.outdatedHorizons ?? [];
        const outgoingHorizons = existingHorizons.filter(
            (horizon) => horizon.provider.toLowerCase() !== action.outdatedHorizon.provider.toLowerCase(),
        );

        return {
            ...state,
            horizonUpdating: false,
            horizonDeleting: false,
            horizonError: null,
            data: {
                ...state.data,
                outdatedHorizons: [...outgoingHorizons, action.outdatedHorizon],
            },
        };
    }),
    on(errorUpdateOutdatedHorizon, (state, action): State => ({
        ...state,
        horizonUpdating: false,
        horizonDeleting: false,
        horizonError: action.error,
    })),
    on(deleteOutdatedHorizon, (state): State => ({
        ...state,
        horizonDeleting: true,
        horizonUpdating: false,
        horizonError: null,
    })),
    on(successDeleteOutdatedHorizon, (state, action): State => ({
        ...state,
        horizonDeleting: false,
        horizonUpdating: false,
        horizonError: null,
        data: {
            ...state.data,
            outdatedHorizons: (state.data.outdatedHorizons ?? []).filter(
                (horizon) => horizon.provider.toLowerCase() !== action.provider.toLowerCase(),
            ),
        },
    })),
    on(errorDeleteOutdatedHorizon, (state, action): State => ({
        ...state,
        horizonDeleting: false,
        horizonUpdating: false,
        horizonError: action.error,
    })),
);

export function reducer(state: State | undefined, action: Action) {
    return masterDataReducer(state, action);
}
